import React from 'react';
import PropTypes from 'prop-types';
import { Table, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { copyToClipboard } from './../helpers';
import { toastr } from './../actions';

const aolRegex = new RegExp('@aol.com');
const microsoftRegex = new RegExp('@outlook.com');
const gmailRegex = new RegExp('@gmail.com');

const copyBuildPath = (build) => {
  let buildPath;
  if (build.platform === 'IMAP / POP email') {
    if (aolRegex.test(build.account)) {
      buildPath = build.outputPath + '\\IMAP___POP_email\\IMAP_AOL_Case';
    } else if (microsoftRegex.test(build.account)) {
      buildPath = build.outputPath + '\\IMAP___POP_email\\POP_Case';
    } else if (gmailRegex.test(build.account)) {
      buildPath = build.outputPath + '\\IMAP___POP_email\\IMAP_Case';
    }
  }
  else {
    buildPath = build.outputPath + '\\' + build.platform;
  }
  copyToClipboard(buildPath);
  return toastr.info('Copied Build Path');
};

const tableStyle = {
  'borderCollapse':'collapse',
  'fontSize':'10px'
};

const tableHeaderStyle = {
  'backgroundColor':'#337ab7',
  color:'white'
};

const successColor = {
  color:'#33AA33',
  'fontSize':'25px',
  'textShadow': '-2px 0 #339933, 0 2px #339933, 2px 0 #339933, 0 -2px #339933'
};

const warningColor = {
  color:'#EEEE33',
  'fontSize':'25px',
  'textShadow': '-2px 0 #AAAA33, 0 2px #AAAA33, 2px 0 #AAAA33, 0 -2px #AAAA33'
};

const popupStyle = { maxWidth: '400px' };
const popupContentStyle = { textAlign:'left' };

const getTooltip = (a) => {
  return (
    <Tooltip id="tooltip" style={popupStyle}>
      <div style={popupContentStyle}>
        {a.errors.join('\n')}
      </div>
    </Tooltip>
  );
};

const bytesToSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const parseSizeDiff = (previous, current) => {
  let diff;
  if (previous >= current) {
    diff = (current === -1) ?
      bytesToSize(previous - current - 1) :
      bytesToSize(previous - current);
  } else {
    diff = (previous === -1) ?
      bytesToSize(current - previous - 1) :
      bytesToSize(current - previous);
  }
  return (
    <td
      className="text-center" style={(diff === '0 Bytes') ?
        {} : { color:'red' }}
    >
      {(diff === '0 Bytes') ? '-' : <strong>{diff}</strong>}
    </td>
  );
};

const parseHitDiff = (previous, current) => {
  const diff = (previous === -1) ?
    current - previous - 1 :
    current - previous;
  return (
    <td
      className="text-center" style={(diff === 0) ?
        {} : { color:'red' }}
    >
      {(diff === 0) ? '-' : <strong>{diff}</strong>}
    </td>
  );
};

export const Overlay = ({ acquisition, children }) => {
  if (!acquisition.errors.length) {
    return children;
  }

  const tooltip = getTooltip(acquisition);
  return (
    <OverlayTrigger id="tooltip" placement="right" overlay={tooltip}>
      {children}
    </OverlayTrigger>
  );
};
Overlay.propTypes = {
  acquisition: PropTypes.shape({
    hitCount: PropTypes.number.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  children: PropTypes.any
};

const CloudAcquisitionTableComponent = ({ acquisitions }) => (
  <div>
    <Table striped bordered style={tableStyle}>
      <thead>
        <tr style={tableHeaderStyle}>
          <th />
          <th>Platform</th>
          <th>Service</th>
          <th>Account</th>
          <th className="text-center">Authenticated</th>
          <th>Started</th>
          <th className="text-center">Duration</th>
          <th className="text-center">Prev Hits</th>
          <th className="text-center">Hits</th>
          <th className="text-center">Difference</th>
          <th className="text-center">Prev Image Size</th>
          <th className="text-center">Image Size</th>
          <th className="text-center">Difference</th>
        </tr>
      </thead>
      <tbody>
        {
          acquisitions.map((a, i) => (
            <tr key={i}>
              <td className="text-center">
                <Overlay acquisition={a}>
                  <span
                    style={
                      (a.hitCount > 0 && !a.errors.length) ?
                        successColor :
                        warningColor
                    }
                    className="glyphicon glyphicon-cloud"
                  />
                </Overlay>
              </td>
              <td><a onClick={() => copyBuildPath(a, a.platform)}>{a.platform}</a></td>
              <td>{a.service}</td>
              <td>{a.account}</td>
              <td className="text-center">
                {a.authenticationSuccess ? 'true' : 'false'}
              </td>
              <td>{a.startTime}</td>
              <td className="text-center">
                {a.duration > 0 ? a.duration + ' seconds' : '-' }
              </td>
              <td className="text-center">
                {a.prevHitCount === -1 ?
                  'n/a' : a.prevHitCount}</td>
              <td className="text-center"><strong>{a.hitCount}</strong></td>
              {parseHitDiff(a.prevHitCount, a.hitCount)}
              <td className="text-center">{bytesToSize(a.prevImageSize)}</td>
              <td className="text-center"><strong>{bytesToSize(a.imageSize)}</strong></td>
              {parseSizeDiff(a.prevImageSize, a.imageSize)}
            </tr>
          ))
        }
      </tbody>
    </Table>
  </div>
);

CloudAcquisitionTableComponent.propTypes = {
  acquisitions : PropTypes.arrayOf(PropTypes.shape({
    platform: PropTypes.string.isRequired,
    service: PropTypes.string.isRequired,
    account: PropTypes.string.isRequired,
    authenticationSuccess: PropTypes.bool,
    startTime: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    prevHitCount: PropTypes.number.isRequired,
    hitCount: PropTypes.number.isRequired,
    warnings: PropTypes.arrayOf(PropTypes.string),
    errors: PropTypes.arrayOf(PropTypes.string).isRequired,
    outputPath: PropTypes.string
  }).isRequired)
};

export default CloudAcquisitionTableComponent;


// WEBPACK FOOTER //
// src/Components/CloudAcquisitionTableComponent.jsx